import { __awaiter } from "tslib";
import React, { useCallback, useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, Paper, TextField, Typography } from "@mui/material";
import { CardElement, IbanElement, Elements, ElementsConsumer, PaymentRequestButtonElement, } from "@stripe/react-stripe-js";
import { resources } from "../resources";
import { styles, theme } from "../theme";
import { PaymentStatus } from "../types";
import { env, fetchStripeClientSecret } from "../request";
import { PaymentIcon } from "../icons";
const messages = resources.getStrings();
const stripeStyle = {
    style: {
        base: {
            fontSize: '16px',
            color: theme.palette.primary.main,
            '::placeholder': {
                color: theme.palette.primary.light,
            }
        },
        invalid: {
            color: theme.palette.error.main
        }
    }
};
const CardPaymentForm = ({ stripe, elements, stripeClientSecret, onPaymentConfirmed, onPaymentError }) => {
    const [saveMethod, setSaveMethod] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState();
    const handleSubmit = useCallback((event) => {
        if (event)
            event.preventDefault();
        setProcessing(true);
        setError(undefined);
        let data;
        if (stripeClientSecret.paymentMethodId !== undefined) {
            data = { payment_method: stripeClientSecret.paymentMethodId };
        }
        else {
            data = {
                payment_method: {
                    card: elements.getElement(CardElement)
                },
                setup_future_usage: saveMethod ? 'off_session' : undefined
            };
        }
        stripe.confirmCardPayment(stripeClientSecret.clientSecret, data)
            .then(({ error, paymentIntent }) => {
            let confirmed = false;
            if (paymentIntent && (paymentIntent.status === 'processing' || paymentIntent.status === 'succeeded')) {
                confirmed = true;
            }
            if (error) {
                if (error.payment_intent && (error.payment_intent.status === 'processing' || error.payment_intent.status === 'succeeded')) {
                    confirmed = true;
                    paymentIntent = error.payment_intent;
                }
                else {
                    console.error('[error]', error);
                    setError(error.message);
                    onPaymentError(error);
                }
            }
            if (confirmed && paymentIntent) {
                onPaymentConfirmed(paymentIntent, saveMethod);
            }
        })
            .finally(() => {
            setProcessing(false);
        });
    }, [stripe, elements, stripeClientSecret, onPaymentError, onPaymentConfirmed]);
    useEffect(() => {
        if (stripeClientSecret.paymentMethodId !== undefined) {
            handleSubmit();
        }
    }, []);
    const toggleSaveMethod = () => setSaveMethod(!saveMethod);
    return (React.createElement("div", null,
        React.createElement(Typography, { variant: "h6", align: "center" },
            React.createElement(PaymentIcon, { style: styles.paymentTitleIcon }),
            React.createElement("strong", null, messages.PaymentMethodCards)),
        React.createElement(Box, { my: 2 }),
        stripeClientSecret.paymentMethodId === undefined && (React.createElement("form", { onSubmit: handleSubmit },
            React.createElement(Paper, { variant: "outlined" },
                React.createElement(Box, { p: 2 },
                    React.createElement(CardElement, { options: Object.assign(Object.assign({}, stripeStyle), { hidePostalCode: true }) }))),
            React.createElement(Box, { my: 1 }),
            React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { checked: saveMethod, onChange: toggleSaveMethod, color: "primary" }), style: { textAlign: 'center', display: 'flex', marginRight: 0 }, label: messages.PaymentFormSavePaymentMethod }),
            React.createElement(Box, { mt: 2, style: { textAlign: 'center' } },
                React.createElement(Button, { type: "submit", style: { minWidth: 150 }, size: "large", variant: "outlined", color: "primary", disabled: !stripe || processing }, "Pay")))),
        processing && (React.createElement(Box, { mt: 2 },
            React.createElement(Typography, { variant: "body1", align: "center" }, messages.ProcessingPayment))),
        error && (React.createElement(Box, { mt: 2 },
            React.createElement(Alert, { severity: "error" }, error)))));
};
export const StripeCardPayment = ({ publicKey, stripeClientSecret, onPaymentError, onPaymentConfirmed }) => {
    const [stripe, setStripe] = useState();
    useEffect(() => {
        loadStripe(publicKey)
            .then(s => setStripe(s || undefined));
    }, []);
    return ((!stripe) ?
        React.createElement(Typography, null, "Loading...") :
        React.createElement(Elements, { stripe: stripe },
            React.createElement(ElementsConsumer, null, ({ elements, stripe }) => (React.createElement(CardPaymentForm, { elements: elements, stripe: stripe, stripeClientSecret: stripeClientSecret, onPaymentError: onPaymentError, onPaymentConfirmed: (paymentIntent, saveMethod) => handlePaymentConfirmed(paymentIntent, saveMethod, onPaymentConfirmed) })))));
};
const SepaPaymentForm = ({ stripe, elements, stripeClientSecret, payeeName, onPaymentError, onPaymentConfirmed }) => {
    const [billingDetails, setBillingDetails] = useState({ name: '', email: '' });
    const [saveMethod, setSaveMethod] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState();
    useEffect(() => {
        if (stripeClientSecret.paymentMethodId !== undefined) {
            handleSubmit();
        }
    }, []);
    const handleSubmit = useCallback((event) => {
        if (event)
            event.preventDefault();
        setProcessing(true);
        setError(undefined);
        let data;
        if (stripeClientSecret.paymentMethodId !== undefined) {
            data = { payment_method: stripeClientSecret.paymentMethodId };
        }
        else {
            if (billingDetails.name.trim().length === 0 || billingDetails.email.trim().length === 0) {
                const top = document.getElementById('payment-flow-box');
                if (top) {
                    top.scrollIntoView();
                }
                setProcessing(false);
                console.log('missing info', billingDetails);
                return;
            }
            data = {
                payment_method: {
                    sepa_debit: elements.getElement(IbanElement),
                    billing_details: billingDetails
                },
                setup_future_usage: saveMethod ? 'off_session' : undefined
            };
        }
        stripe.confirmSepaDebitPayment(stripeClientSecret.clientSecret, data)
            .then(({ error, paymentIntent }) => {
            setProcessing(true);
            let confirmed = false;
            if (paymentIntent && (paymentIntent.status === 'processing' || paymentIntent.status === 'succeeded')) {
                confirmed = true;
            }
            if (error) {
                if (error.payment_intent && (error.payment_intent.status === 'processing' || error.payment_intent.status === 'succeeded')) {
                    confirmed = true;
                    paymentIntent = error.payment_intent;
                }
                else {
                    console.error('[error]', error);
                    setError(error.message);
                    onPaymentError(error);
                }
            }
            if (confirmed && paymentIntent) {
                onPaymentConfirmed(paymentIntent, saveMethod);
            }
        })
            .finally(() => setProcessing(false));
    }, [stripe, elements, stripeClientSecret, onPaymentError, onPaymentConfirmed, billingDetails, setProcessing]);
    const toggleSaveMethod = () => setSaveMethod(!saveMethod);
    const onBillingDetailsChange = (event) => {
        setBillingDetails(Object.assign(Object.assign({}, billingDetails), { [event.target.name]: event.target.value }));
    };
    return (React.createElement("div", null,
        React.createElement(Typography, { variant: "h6", align: "center" },
            React.createElement(PaymentIcon, { style: styles.paymentTitleIcon }),
            React.createElement("strong", null, messages.PaymentMethodSepaDirectDebit)),
        React.createElement(Box, { my: 2 }),
        stripeClientSecret.paymentMethodId === undefined && (React.createElement("form", { onSubmit: handleSubmit },
            React.createElement(Grid, { container: true, spacing: 1 },
                React.createElement(Grid, { item: true, xs: 12, md: 6 },
                    React.createElement(TextField, { fullWidth: true, required: true, label: messages.Name, variant: "outlined", name: "name", value: billingDetails.name, onChange: onBillingDetailsChange })),
                React.createElement(Grid, { item: true, xs: 12, md: 6 },
                    React.createElement(TextField, { fullWidth: true, required: true, label: messages.Email, variant: "outlined", type: "email", name: "email", value: billingDetails.email, onChange: onBillingDetailsChange }))),
            React.createElement(Box, { my: 1 }),
            React.createElement(Paper, { variant: "outlined" },
                React.createElement(Box, { p: 2 },
                    React.createElement(IbanElement, { options: Object.assign(Object.assign({}, stripeStyle), { supportedCountries: ['SEPA'], placeholderCountry: 'AT' }) }))),
            React.createElement(Box, { my: 1 }),
            React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { checked: saveMethod, onChange: toggleSaveMethod, color: "primary" }), style: { textAlign: 'center', display: 'flex', marginRight: 0 }, label: messages.PaymentFormSavePaymentMethod }),
            React.createElement(Box, { my: 1 }),
            React.createElement(Typography, { variant: "body2", align: "center", style: { fontSize: '0.8rem' } }, messages.PaymentFormSepaDirectDebitAcceptance.replace(/%PAYEE%/g, payeeName)),
            React.createElement(Box, { mt: 2, style: { textAlign: 'center' } },
                React.createElement(Button, { type: "submit", style: { minWidth: 150 }, size: "large", variant: "outlined", color: "primary", disabled: !stripe || processing }, "Pay")))),
        processing && (React.createElement(Box, { mt: 2 },
            React.createElement(Typography, { variant: "body1", align: "center" }, messages.ProcessingPayment))),
        error && (React.createElement(Box, { mt: 2 },
            React.createElement(Alert, { severity: "error" }, error)))));
};
export const StripeSepaPayment = ({ publicKey, payeeName, stripeClientSecret, onPaymentConfirmed, onPaymentError }) => {
    const [stripe, setStripe] = useState();
    useEffect(() => {
        loadStripe(publicKey)
            .then(s => setStripe(s || undefined));
    }, []);
    return ((!stripe) ?
        React.createElement(Typography, null, "Loading...") :
        React.createElement(Elements, { stripe: stripe },
            React.createElement(ElementsConsumer, null, ({ elements, stripe }) => (React.createElement(SepaPaymentForm, { elements: elements, stripe: stripe, stripeClientSecret: stripeClientSecret, payeeName: payeeName, onPaymentError: onPaymentError, onPaymentConfirmed: (paymentIntent, saveMethod) => handlePaymentConfirmed(paymentIntent, saveMethod, onPaymentConfirmed) })))));
};
const PaymentRequestForm = ({ stripe, country, currency, payeeName, amount, onPaymentSelect, onPaymentCancel, onPaymentError, onPaymentConfirmed, onNotSupported }) => {
    const [paymentRequest, setPaymentRequest] = useState();
    useEffect(() => {
        const pr = stripe.paymentRequest({
            country: country,
            currency: currency.toLowerCase(),
            total: {
                label: payeeName,
                amount: Math.ceil(amount * 100),
            }
        });
        pr.canMakePayment()
            .then(result => {
            if (result) {
                setPaymentRequest(pr);
            }
            else {
                onNotSupported();
            }
        });
        pr.on('paymentmethod', (ev) => __awaiter(void 0, void 0, void 0, function* () {
            const { clientSecret } = yield fetchStripeClientSecret(env.paymentId);
            onPaymentSelect();
            // Confirm the PaymentIntent without handling potential next actions (yet).
            const { paymentIntent, error: confirmError } = yield stripe.confirmCardPayment(clientSecret, { payment_method: ev.paymentMethod.id }, { handleActions: false });
            if (confirmError) {
                // Report to the browser that the payment failed, prompting it to
                // re-show the payment interface, or show an error message and close
                // the payment interface.
                ev.complete('fail');
                onPaymentError(confirmError);
            }
            else {
                // Report to the browser that the confirmation was successful, prompting
                // it to close the browser payment method collection interface.
                ev.complete('success');
                // Check if the PaymentIntent requires any actions and if so let Stripe.js
                // handle the flow. If using an API version older than "2019-02-11"
                // instead check for: `paymentIntent.status === "requires_source_action"`.
                if (paymentIntent) {
                    if (paymentIntent.status === "requires_action") {
                        // Let Stripe.js handle the rest of the payment flow.
                        const { error } = yield stripe.confirmCardPayment(clientSecret);
                        if (error) {
                            // The payment failed -- ask your customer for a new payment method.
                            onPaymentError(error);
                            return;
                        }
                    }
                    // The payment has succeeded.
                    onPaymentConfirmed(paymentIntent);
                }
                else {
                    // The payment has succeeded.
                    onPaymentConfirmed();
                }
            }
        }));
        pr.on('cancel', onPaymentCancel);
    }, []);
    return (React.createElement("div", { style: { width: '100%' } }, paymentRequest && (React.createElement(PaymentRequestButtonElement, { options: { paymentRequest } }))));
};
export const StripePaymentRequest = ({ publicKey, currency, amount, country, payeeName, onPaymentError, onPaymentConfirmed, onNotSupported, onSelected, onCanceled }) => {
    const [stripe, setStripe] = useState();
    useEffect(() => {
        loadStripe(publicKey)
            .then(s => setStripe(s || undefined));
    }, []);
    return ((!stripe) ?
        React.createElement(Typography, null, "Loading...") :
        React.createElement(Elements, { stripe: stripe },
            React.createElement(ElementsConsumer, null, ({ stripe }) => (React.createElement(PaymentRequestForm, { stripe: stripe, currency: currency, amount: amount, country: country, onPaymentSelect: onSelected, onPaymentCancel: onCanceled, onPaymentError: onPaymentError, onPaymentConfirmed: paymentIntent => handlePaymentConfirmed(paymentIntent || { status: 'succeeded' }, false, onPaymentConfirmed), payeeName: payeeName, onNotSupported: onNotSupported })))));
};
function handlePaymentConfirmed(paymentIntent, saveMethod, onPaymentConfirmed) {
    let status;
    switch (paymentIntent.status) {
        case 'processing':
            status = PaymentStatus.Processing;
            break;
        case 'canceled':
            status = PaymentStatus.Canceled;
            break;
        case 'succeeded':
            status = PaymentStatus.Succeeded;
            break;
        default:
            status = PaymentStatus.Failed;
            break;
    }
    onPaymentConfirmed(status, saveMethod);
}
//# sourceMappingURL=stripe.js.map